/**
 * SirenLookup — Autocomplete search widget for French company lookup.
 *
 * Pure search mode: finds companies, enriches on selection, emits CompanyData.
 * Does NOT hold selected state — parent owns the data via CompanyDataForm.
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { companyLookupService, type CompanyData, type CompanyPreview } from '@/lib/company-lookup.service'

interface SirenLookupProps {
  /** Called when a company is selected and enriched */
  onCompanySelected: (data: CompanyData) => void
  /** Whether the input is disabled */
  disabled?: boolean
  /** Accent color class prefix (e.g. "amber" or "violet") */
  accent?: string
}

export default function SirenLookup({
  onCompanySelected,
  disabled = false,
  accent = 'amber',
}: SirenLookupProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<CompanyPreview[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isEnriching, setIsEnriching] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([])
      setShowDropdown(false)
      return
    }

    setIsSearching(true)
    setError(null)
    try {
      const data = await companyLookupService.searchCompanies(q, 6)
      setResults(data)
      setShowDropdown(data.length > 0)
      if (data.length === 0 && q.length >= 3) {
        setError('No companies found')
      }
    } catch {
      setError('Search failed — try again')
      setResults([])
    } finally {
      setIsSearching(false)
    }
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setQuery(val)
    setError(null)

    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (val.length < 2) {
      setResults([])
      setShowDropdown(false)
      return
    }

    debounceRef.current = setTimeout(() => doSearch(val), 300)
  }

  const handleSelect = async (preview: CompanyPreview) => {
    setShowDropdown(false)
    setQuery('')
    setResults([])
    setIsEnriching(true)
    setError(null)

    try {
      const profile = await companyLookupService.getCompanyProfile(preview.siren)
      const data = companyLookupService.profileToCompanyData(profile)
      onCompanySelected(data)
    } catch {
      setError('Could not enrich company data — try again')
    } finally {
      setIsEnriching(false)
    }
  }

  const accentRing = `focus:ring-${accent}-500/50 focus:border-${accent}-500`

  // --- Search input + dropdown ---
  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
          placeholder="Search by company name or SIREN..."
          disabled={disabled || isEnriching}
          className={`w-full px-4 py-3 pr-10 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-400 focus:outline-none focus:ring-2 ${accentRing} transition-all disabled:opacity-60`}
        />
        {/* Spinner or search icon */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {isSearching || isEnriching ? (
            <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
          ) : (
            <svg className="w-4 h-4 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          )}
        </div>
      </div>

      {/* Enriching label */}
      {isEnriching && (
        <p className="text-xs text-slate-400 mt-1.5">Loading company details...</p>
      )}

      {/* Error / no results */}
      {error && !showDropdown && !isEnriching && (
        <p className="text-xs text-slate-400 mt-1.5">{error}</p>
      )}

      {/* Dropdown results */}
      {showDropdown && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden max-h-64 overflow-y-auto">
          {results.map((r) => (
            <button
              key={r.siren}
              type="button"
              onClick={() => handleSelect(r)}
              className="w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors border-b border-slate-100 dark:border-slate-700/50 last:border-0"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                    {r.name}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {r.siren}
                    {r.city && ` · ${r.city}`}
                    {r.naf_label && ` · ${r.naf_label}`}
                  </p>
                </div>
                {!r.is_active && (
                  <span className="shrink-0 text-[10px] font-bold text-red-500 bg-red-500/10 px-2 py-0.5 rounded-full">
                    Closed
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
